import RelationshipAreYouSure from './RelationshipAreYouSure';
import Warning from '../../core/Warning';
import Grid from '@material-ui/core/Grid';
import LabelIcon from '@material-ui/icons/Label';
import GroupWorkIcon from '@material-ui/icons/GroupWork';
import HierarchyIcon from '../../core/icons/Hierarchy';
import cloneDeep from 'lodash/cloneDeep';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { APPLICATION_PATHS, ENTITLEMENTS } from '../../application-core/constants';
import ComponentTitle from '../../core/ComponentTitle';
import InAppBar from '../../core/InAppBar';
import CancelChange from '../../core/CancelChange';
import { checkResourceEntitlements, processHashLocation, makeCancelable } from '../../core/utils/utils';
import withGS from '../../core/withGShoc';
import GS from '../../grafsync/grafsync';
import LabelView from './LabelView';
import ConceptView from './ConceptView';
import TaxonomyView from './TaxonomyView';
import { STATUS_DRAFT } from '../../core/utils/constants';


const tabValues = {
  0: "concept",
  1: "bucket",
  2: "label"
};

class TaxonomyEditorContainer extends Component {

  constructor(props) {
    super(props);

    this.state = {
      move: props.data.editingMode === "Mono",
      originalEditingMode: props.data.editingMode,
      tabValue: props.data.editingMode === "Flat" ? 1 : 0,
      updateAt: null,
      viewWidth: 0,
      isFullView: false,
      foundRef: null,
      hasScrolled: false,
      conceptChanged: false,
      viewLang: props.defaultLang,
      deletedLabels: [],
      deletedConcepts: [],
      titlePath: [],
      paths: [],
      activeLabel: null,
      activeConcept: null,
      confirmPropertyRemoval: false,
      foundPath: null,
      isErrored: false,
      fromPath: false
    };
    this.cancellable = null;
  }

  static getDerivedStateFromProps(props, state) {
    const paramsMap = processHashLocation(props.location);
    let concept = paramsMap.get("concept");
    const escape = paramsMap.get("escape");
    if (escape === "true") concept = concept.replace("%23", '#');
    const label = paramsMap.get("label");
    const view = paramsMap.get("view");
    let tab = view === "bucket" ?
      1
      :
      view === "concept" || !props.skosXL ?
        0
        :
        2;
    const flatView = props.data.editingMode === "Flat";
    if (flatView && tab !== 2) tab = 1;
    // If another user changes this we need to pick up the change - via props
    const move = props.data.editingMode === "Mono";
    // But we only need to override the local state if locally we're not aligned with the updated prop
    const editingModeChanged = move !== state.move && props.data.editingMode !== state.originalEditingMode;
    if (editingModeChanged || (tab !== state.tabValue) || (label !== state.activeLabel) || (concept !== state.activeConcept)) {
      return {
        ...(editingModeChanged) && {
          move
        },
        ...(view) && { tabValue: tab },
        ...(state.activeConcept !== concept) &&
        { activeConcept: (concept === undefined || concept === null || concept === "") ? null : concept },
        ...(state.activeLabel !== label) &&
        { activeLabel: (label === undefined || label === null || label === "") ? null : label }
      };
    }
    return null;
  }

  buildLink(view, concept, label) {
    let link = APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/taxonomy#project='
      + this.props.project + "&taxonomy=" + this.props.id + "&view=" + view;
    link += concept !== null ? "&concept=" + concept : "";
    link += label !== null ? "&label=" + label : "";
    return link;
  }

  setMode = (move) => this.setState({ move });

  toggleTaxonomyDetail = () => this.props.toggleTaxonomyDetail(0);

  changeTab = (tabValue) => {
    const view = tabValues[tabValue];
    const link = this.buildLink(view, this.state.activeConcept, this.state.activeLabel);
    this.props.history.push(link);
  };

  componentWillUnmount() {
    clearTimeout(this.langTimeout);
    if (this.cancellable) this.cancellable.cancel();
  }

  /* Returns an array of IRIs that is the path to the found concept */
  findConcept = async (iri, pathData) => {
    const { data } = this.props;
    // If it's a top concept it will already be loaded
    if (data.hasTopConcept && data.hasTopConcept.includes(iri)) {
      const expansionPath = "<" + iri + ">";
      this.setState({
        foundPath: expansionPath,
        foundRef: React.createRef(),
        foundTime: Date.now()
      });
      return [iri];
    }
    else {
      try {
        const searchData = pathData || await this.doPath({
          graph: this.props.graph,
          source: iri,
          path: "http://www.w3.org/2004/02/skos/core#broader"
        }).promise;
        if (this.cancellable && this.cancellable.isCancelled()) return;
        if (searchData.results) {
          const hasTopConceptResult = this.props.data.hasTopConcept ? searchData.results.find(result => this.props.data.hasTopConcept.includes(result.parent)) : null;
          if (hasTopConceptResult) {
            const hasTopConcept = hasTopConceptResult.parent;
            const expansionPath = this.getExpansionTree(hasTopConcept, searchData.results, iri, "<" + hasTopConcept + ">");
            this.getFoundConcept(expansionPath, pathData);
            // Now select the found concept
            let pathParts = expansionPath.substring(1, expansionPath.length - 1).split("><");
            return pathParts;
          }
        }
      }
      catch (e) {
        if (this.cancellable && this.cancellable.isCancelled()) return;
        this.setState({
          isErrored: true,
          message: "There was an issue processing information for a concept."
        });
      }
    }
  };

  doPath(payload) {
    this.cancellable = makeCancelable(GS.path(payload));
    return this.cancellable;
  }

  getFoundConcept(expansionPath) {
    // We need to expand all the nodes down to the found node if not already expanded
    let paths = cloneDeep(this.state.paths);
    let pathParts = expansionPath.substring(1, expansionPath.length - 1).split("><");
    pathParts.pop();
    let path = "";
    pathParts.forEach(part => {
      path += "<" + part + ">";
      if (!paths.includes(path)) paths.push(path);
    });
    this.setState({
      paths
    }, () => {
      // Only when we have expanded do we try and find
      this.setState({
        foundPath: expansionPath,
        foundRef: React.createRef(),
        foundTime: Date.now()
      });
    });
  }

  // There could be multiple paths to the concept but we follow the first
  getExpansionTree(parentConcept, results, foundIRI, path = "") {
    let childConcepts = results.filter(result => result.parent === parentConcept).map(result => result.child);
    if (!childConcepts) return path;
    if (childConcepts.includes(foundIRI)) return path + "<" + foundIRI + ">";
    return this.getExpansionTree(childConcepts[0], results, foundIRI, path + "<" + childConcepts[0] + ">");
  }

  onExitWarning = () => this.setState({ isErrored: false });

  componentDidMount() {
    if (this.state.activeConcept && this.state.tabValue === 0) this.setFoundConcept(this.state.activeConcept);
  }

  setFoundConcept = async (iri, titlePath) => {
    if (titlePath && titlePath.length > 0) {
      this.showDetail(iri, [...titlePath, iri]);
      const path = [...titlePath, iri].map(p => "<" + p + ">").join("");
      this.getFoundConcept(path);
      this.setState({
        fromPath: true,
        titlePath: [...titlePath, iri]
      });
      return;
    }
    this.showDetail(iri);
    let foundPath = await this.findConcept(iri);
    if (this.cancellable && this.cancellable.isCancelled()) return;
    if (foundPath) {
      this.setState({ titlePath: foundPath });
    }
    else {
      this.setState({ titlePath: [] });
    }
  };

  componentDidUpdate() {
    GS.setStore("vm", this.props.location.pathname + this.props.location.hash);
    if (this.state.tabValue === 0 &&
      (!this.state.fromPath && this.state.runSearch && this.state.titlePath && this.state.titlePath.length > 0
        && this.state.activeConcept !== this.state.titlePath[this.state.titlePath.length - 1])) {
      this.setState({ titlePath: [] }, () => {
        if (this.state.activeConcept) this.setFoundConcept(this.state.activeConcept);
      });
    }
  }

  expandConcept = (path) => {
    let paths = cloneDeep(this.state.paths);
    paths.push(path);
    this.setState({ paths });
  };

  collapseConcept = (collapsePath) => {
    let paths = cloneDeep(this.state.paths);
    let pathIndex = paths.indexOf(collapsePath);
    paths.splice(pathIndex, 1);
    this.setState({ paths });
  };

  showDetail = (iri, path, runSearch = true) => {
    const link = this.buildLink("concept", iri, this.state.activeLabel);
    this.setState({ runSearch }, () => {
      const currentPath = this.props.location.pathname + this.props.location.hash;
      if (currentPath !== link) this.props.history.push(link);
      this.setState({
        ...(path) && { titlePath: [...path] },
        runSearch: true,
        fromPath: false
      });
    });
  };

  showIsolatedDetail = (iri) => {
    const link = this.buildLink("bucket", iri, this.state.activeLabel);
    this.props.history.push(link);
  };

  breakLink = (parentIRI, property, childIRI) => {
    GS.deleteProperty(parentIRI, this.props.graph, property, childIRI);
    if (this.state.activeConcept === childIRI) this.showDetail(null);
  };

  addToScheme = (conceptIRI) => {
    GS.addProperty(conceptIRI, this.props.graph, "inScheme", this.props.id);
  };

  doFind(payload) {
    this.cancellable = makeCancelable(GS.find(payload));
    return this.cancellable;
  }

  addNewConcept = (titlePath, path) => async (property, parentIRI, label, reservationID, conceptIRI, createAsXL, labelReservationID, labelIRI) => {
    try {
      const defaultClasses = this.props.data.defaultClass || [];
      const newConcept = {
        id: conceptIRI,
        type: ["Concept", ...defaultClasses],
        ...(this.props.data.activateWorkflow) && { status: [STATUS_DRAFT] },
        inScheme: [this.props.id]
      };
      if (!createAsXL && !labelIRI) {
        newConcept.prefLabel = { [this.props.defaultLang]: label };
      }
      const additionalChannels = [this.props.id];
      // Will ultimately replace these two calls with one combined GS call that effectively does do both
      await GS.insertResource(newConcept, conceptIRI, this.props.graph, additionalChannels, reservationID);
      if (createAsXL) {
        await this.addNewLabel(label, this.props.defaultLang, labelReservationID, labelIRI, false);
        GS.addProperty(conceptIRI, this.props.graph, "xlPrefLabel", labelIRI);
      }
      else if (labelIRI) {
        // Make sure label is loaded first or may not display properly
        await GS.loadData([labelIRI], {}, this.props.graph);
        GS.addProperty(conceptIRI, this.props.graph, 'xlPrefLabel', labelIRI);
      }
      GS.addProperty(parentIRI, this.props.graph, property, conceptIRI);
      if (this.cancellable && this.cancellable.isCancelled()) return;
      this.showDetail(conceptIRI, [...titlePath, conceptIRI]);
      this.expandConcept(path);
      this.setState({
        foundPath: path + "<" + conceptIRI + ">",
        foundRef: React.createRef(),
        foundTime: Date.now()
      });
    }
    catch (e) {
      if (this.cancellable && this.cancellable.isCancelled()) return;
      this.setState({
        isErrored: true,
        message: "There was an issue adding new concept."
      });
    }
  };

  addExistingConcept = (titlePath, path) => (parentIRI, property, childIRI) => {
    // The child might not be loaded so we do that first
    GS.loadData([childIRI], {}, this.props.graph, true).then(async () => {
      if (GS.isLoaded(childIRI, this.props.graph)) {
        const childData = GS.data(childIRI, this.props.graph);
        const move = this.state.move;
        // We only need to check if in monohierarchy mode
        const applyRelationship = () => {
          const latestChildData = GS.data(childIRI, this.props.graph);
          if (this.state.confirmConceptVersion && latestChildData._version !== this.state.confirmConceptVersion) {
            this.setState({ conceptChanged: true });
            return;
          }
          if (childData.inScheme && childData.inScheme.includes(this.props.id)) {
            // So it's already in this scheme somewhere
          }
          else {
            // If not add it to this scheme
            GS.addProperty(childIRI, this.props.graph, 'inScheme', this.props.id);
          }
          // Now add the concept into the tree
          GS.addProperty(parentIRI, this.props.graph, property, childIRI);
          if (move) {
            // We now remove existing inScheme broader/narrower relations for this scheme
            const existingRels = childData["broader"] || [];
            existingRels.forEach(item => {
              if (GS.isLoaded(item, this.props.graph)) {
                const relData = GS.data(item, this.props.graph);
                if (relData.inScheme && relData.inScheme.includes(this.props.id)) {
                  GS.deleteProperty(item, this.props.graph, "narrower", childData.id);
                }
              }
            });
            if (childData.topConceptOf && childData.topConceptOf.includes(this.props.id)) {
              GS.deleteProperty(childData.id, this.props.graph, "topConceptOf", this.props.id);
            }
          }
          if (this.cancellable && this.cancellable.isCancelled()) return;
          this.showDetail(childIRI, [...titlePath, childIRI], false);
          this.expandConcept(path);
          this.setState({
            foundPath: path + "<" + childIRI + ">",
            foundRef: React.createRef(),
            foundTime: Date.now()
          });
        };
        // We supply the things we need to do on confirmation as a callback
        this.setState({ applyRelationship }, async () => {
          const needToCheck = move ? await this.checkIncludeExistingConcept(childData, applyRelationship) : false;
          if (!needToCheck) applyRelationship();
        });
      }
      else {
        this.setState({
          isErrored: true,
          message: "This concept has just been deleted and cannot be added."
        });
      }
    });
  };

  onNotSure = () => this.setState({ confirmPropertyRemoval: false });

  onPropertySure = async () => {
    this.setState({ confirmPropertyRemoval: false });
    const applyRelationship = this.state.applyRelationship;
    applyRelationship();
  };

  checkIncludeExistingConcept = async (data, applyRelationship) => {
    const { graph } = this.props;
    let hasRels = [];
    const existingRels = data["broader"] || [];
    const hasTopConcept = data.topConceptOf && data.topConceptOf.includes(this.props.id) ? true : false;
    if (existingRels.length === 0 && !hasTopConcept) return false;
    // Load the concepts we need to check = hopefully mostly loaded already
    if (existingRels.length > 0) {
      await GS.loadData(existingRels, {}, graph, true);
      // If we are in move mode we now check if we would remove existing inScheme broader/narrower relations for this scheme
      existingRels.forEach(item => {
        if (GS.isLoaded(item, graph)) {
          const relData = GS.data(item, graph);
          if (relData.inScheme && relData.inScheme.includes(this.props.id)) hasRels.push(item);
        }
      });
    }
    if (this.cancellable && this.cancellable.isCancelled()) return;
    if (hasRels || hasTopConcept) {
      this.setState({
        confirmPropertyRemoval: true,
        confirmProperty: "narrower",
        confirmPropertyIRI: data.id,
        confirmNewIRI: data.id,
        confirmIsTopConcept: hasTopConcept,
        confirmConceptList: hasRels,
        confirmPropertyFunction: applyRelationship,
        confirmConceptVersion: data._version
      });
      return true;
    }
    return false;
  };

  deleteConcept = (iri) => {
    GS.deleteResource(iri, this.props.graph);
    // We need to remove all expansion paths that include this IRI
    let paths = cloneDeep(this.state.paths);
    paths.filter(path => !path.includes("<" + iri + ">"));
    const deletedConceptsList = [...this.state.deletedConcepts];
    deletedConceptsList.push(iri);
    this.setState({
      updateAt: Date.now(),
      deletedConcepts: deletedConceptsList,
      paths
    });
    if (this.state.tabValue === 0) {
      this.showDetail(null);
    }
    else {
      // For concepts tab
      this.showIsolatedDetail(null);
    }
  };

  addNewLabel = async (label, lang, reservationID, labelIRI, showDetail = true) => {
    try {
      const newXLlabel = {
        id: labelIRI,
        type: "Label",
        ...(this.props.data.activateWorkflow) && { status: [STATUS_DRAFT] },
        inScheme: [this.props.id],
        literalForm: { [lang]: label }
      };
      const additionalChannels = [this.props.id];
      await GS.insertResource(newXLlabel, labelIRI, this.props.graph, additionalChannels, reservationID);
      this.setState({ updateAt: Date.now() });
      if (showDetail) this.showLabelDetail(labelIRI);
    }
    catch (e) {
      if (this.cancellable && this.cancellable.isCancelled()) return;
      this.setState({
        isErrored: true,
        message: "There was an issue adding new label."
      });
    }
  };

  addExistingLabel = async (labelIRI) => {
    // The child might not be loaded so we do that first
    GS.loadData([labelIRI], {}, this.props.graph).then(() => {
      if (GS.isLoaded(labelIRI, this.props.graph)) {
        const labelData = GS.data(labelIRI, this.props.graph);
        if (labelData.inScheme && labelData.inScheme.includes(this.props.id)) {
          // So it's already in this scheme somewhere
        }
        else {
          // If not add it to this scheme
          GS.addProperty(labelIRI, this.props.graph, 'inScheme', this.props.id);
        }
        this.setState({ updateAt: Date.now() });
        this.showLabelDetail(labelIRI);
      }
      else {
        this.setState({
          isErrored: true,
          message: "This label has just been deleted and cannot be added."
        });
      }
    });
  };

  deleteLabel = (iri, conceptId, label, lang, labelProperty) => {
    // If label is used by a concept as its prefLabel and it is the default language we need to change the concepts to use prefLabel
    if (conceptId && labelProperty === "prefLabel" && lang === this.props.defaultLang) {
      GS.replace(conceptId, this.props.graph, "xlPrefLabel", iri, null, null, "prefLabel", label, lang);
    }
    GS.deleteResource(iri, this.props.graph);
    const deletedLabelList = [...this.state.deletedLabels];
    deletedLabelList.push(iri);
    this.setState({ updateAt: Date.now(), deletedLabels: deletedLabelList });
    this.showLabelDetail(null);
  };

  collapse = () => {
    this.setState({
      paths: []
    });
  };

  showLabelDetail = (iri) => {
    const link = this.buildLink("label", this.state.activeConcept, iri);
    this.props.history.push(link);
  };

  showFloatingLabel = (iri) => {
    const link = APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/floating#project=' + this.props.project + "&view=label&label=" + iri;
    this.props.history.push(link);
  };

  setViewLanguage = (lang) => {
    // To avoid API overloading we wait until any outstanding requests have completed before changing language
    const inFlight = GS.isLoadingInFlight();
    if (inFlight) {
      if (this.langTimeout) clearTimeout(this.langTimeout);
      this.langTimeout = setTimeout(() => this.setViewLanguage(lang), 250);
    }
    else {
      this.setState({ viewLang: lang });
      if (this.state.activeConcept) this.findConcept(this.state.activeConcept);
    }
  };

  onExitChange = () => this.setState({ conceptChanged: false });

  onFullViewChange = () => this.setState({ isFullView: !this.state.isFullView });

  onViewWidthChange = () => this.setState({ viewWidth: this.state.viewWidth === 3 ? 0 : this.state.viewWidth + 1 });

  render() {

    const { activeLabel, deletedLabels, deletedConcepts, updateAt, tabValue, foundPath, foundRef, foundTime,
      isErrored, activeConcept, paths, viewLang, titlePath, message, isFullView, viewWidth,
      confirmPropertyIRI, confirmIsTopConcept, move, confirmPropertyRemoval, confirmConceptList, conceptChanged } = this.state;
    const { id, data, skosXL, defaultLang, project, graph, entitlements, taxonomies, collections, projectUsers, ontologyProjects } = this.props;
    const { useXL, showIRI, editingMode } = data;

    if (isErrored) return <Warning message={message} onExitWarning={this.onExitWarning} />;

    const iriOptions = {
      showIRI,
      initialLowerCase: data.propertiesInitialLowerCase,
      removeSpaces: data.iriRemoveSpaces,
      replaceSpaces: data.iriReplaceSpaces,
      handleIRIclash: data.iriHandleIRIclash ?
        data.iriHandleIRIclash.includes("http://www.w3.org/2008/05/skos-xl#Label")
        :
        false,
      removeSpecialCharacters: data.iriRemoveSpecialCharacters,
      replaceSpecialCharacters: data.iriReplaceSpecialCharacters,
      minimiseEncoding: data.minimiseEncoding,
      xlOptions: {
        prependText: "iriPrependLabel" in data ?
          data.iriPrependLabel ?
            data.iriPrependLabelText || "label"
            :
            ""
          :
          data.iriPrependLabelText || "label",
        shouldAppendText: "iriAppendLabelWithLanguageCode" in data ? data.iriAppendLabelWithLanguageCode : true
      }
    };

    const isReadOnly = checkResourceEntitlements(data, ENTITLEMENTS.EDIT_TAXONOMY);
    const flatView = editingMode === "Flat";

    let tabItems = [
      {
        icon: HierarchyIcon,
        label: "Hierarchy",
        disabled: flatView
      },
      {
        icon: GroupWorkIcon,
        label: "Concepts"
      }
    ];
    if (skosXL) tabItems.push({
      icon: LabelIcon,
      label: "Labels"
    });

    const appBar = <InAppBar
      tabValue={tabValue}
      onChangeTab={this.changeTab}
      tabItems={tabItems} />;

    return (
      <React.Fragment>
        <ComponentTitle title={data.title} id={data.id} viewLang={viewLang} defaultLang={defaultLang} onClick={this.toggleTaxonomyDetail} />
        <Grid container spacing={0}>
          <Grid item xs={12}>
            {tabValue === 0 &&
              <TaxonomyView
                appBar={appBar}
                entitlements={entitlements}
                onSetMode={this.setMode}
                move={move}
                graph={graph}
                projectUsers={projectUsers}
                iriOptions={iriOptions}
                useCustomSort={"sortProperty" in data && data.sortProperty !== undefined}
                customSort={{
                  "property": data.sortProperty,
                  "sortNumerically": data.sortNumerically
                }}
                isReadOnly={isReadOnly}
                expandedPaths={paths}
                onCollapse={this.collapse}
                collections={collections}
                taxonomies={taxonomies}
                ontologyProjects={ontologyProjects}
                activeConcept={activeConcept}
                expandConcept={this.expandConcept}
                collapseConcept={this.collapseConcept}
                foundPath={foundPath}
                foundRef={foundRef}
                foundTime={foundTime}
                selectedTitlePath={titlePath}
                titlePath={[]}
                skosXL={skosXL}
                isFullView={isFullView}
                viewWidth={viewWidth}
                onViewWidthChange={this.onViewWidthChange}
                onFullViewChange={this.onFullViewChange}
                editingMode={data.editingMode}
                project={project}
                useSlashIRI={data.useSlashIRI || false}
                useUuidIRI={data.useUuidIRI || false}
                useCounterIRI={data.useCounterIRI || false}
                counters={data.useCounterIRI ?
                  {
                    primaryCounter: data.primaryCounter,
                    secondaryCounter: data.secondaryCounter
                  }
                  :
                  {}
                }
                onSelectViewLanguage={this.setViewLanguage}
                onAddNewConcept={this.addNewConcept}
                onDeleteConcept={this.deleteConcept}
                onFoundConcept={this.setFoundConcept}
                onAddExistingConcept={this.addExistingConcept}
                defaultLang={defaultLang}
                viewLang={viewLang}
                onSelect={this.showDetail}
                onBreakLink={this.breakLink}
                addToScheme={this.addToScheme}
                version={data.version}
                showFloatingLabel={this.showFloatingLabel}
                showLabelDetail={this.showLabelDetail}
                data={data}
                schemeIRI={id}
              />
            }
            {tabValue === 1 &&
              <ConceptView
                appBar={appBar}
                entitlements={entitlements}
                graph={graph}
                flat={data.editingMode === "Flat"}
                projectUsers={projectUsers}
                iriOptions={iriOptions}
                isReadOnly={isReadOnly}
                updateAt={updateAt}
                project={project}
                titlePath={[]}
                enableTSV={true}
                defaultClasses={data.defaultClass}
                hiddenProps={data.hiddenProp}
                activeConcept={activeConcept}
                defaultLang={defaultLang}
                viewLang={viewLang}
                skosXL={skosXL}
                useXL={useXL}
                useSlashIRI={data.useSlashIRI || false}
                useUuidIRI={data.useUuidIRI || false}
                useCounterIRI={data.useCounterIRI || false}
                counters={data.useCounterIRI ?
                  {
                    primaryCounter: data.primaryCounter,
                    secondaryCounter: data.secondaryCounter
                  }
                  :
                  {}
                }
                onSelect={this.showIsolatedDetail}
                ontologyProjects={ontologyProjects}
                addToScheme={this.addToScheme}
                onSelectViewLanguage={this.setViewLanguage}
                deletedConcepts={deletedConcepts}
                onAddNewConcept={this.addNewConcept}
                onDeleteConcept={this.deleteConcept}
                onFoundConcept={this.setFoundConcept}
                onAddExistingConcept={this.addExistingConcept}
                showFloatingLabel={this.showFloatingLabel}
                showLabelDetail={this.showLabelDetail}
                data={data}
                schemeIRI={id}
              />
            }
            {tabValue === 2 &&
              <LabelView
                appBar={appBar}
                graph={graph}
                projectUsers={projectUsers}
                entitlements={entitlements}
                iriOptions={iriOptions}
                isReadOnly={isReadOnly}
                updateAt={updateAt}
                project={project}
                defaultLang={defaultLang}
                activeLabel={activeLabel}
                onSelect={this.showLabelDetail}
                onAddExistingLabel={this.addExistingLabel}
                onAddNewLabel={this.addNewLabel}
                deletedLabels={deletedLabels}
                onDeleteLabel={this.deleteLabel}
                data={data}
                schemeIRI={id}
                useSlashIRI={data.useSlashIRI || false}
                useUuidIRI={data.useUuidIRI || false}
                useCounterIRI={data.useCounterIRI || false}
                counters={data.useCounterIRI ?
                  {
                    primaryCounter: data.primaryCounter,
                    secondaryCounter: data.secondaryCounter
                  }
                  :
                  {}
                }
              />
            }
          </Grid>
        </Grid>
        {confirmPropertyRemoval &&
          <RelationshipAreYouSure
            graph={graph}
            source={confirmPropertyIRI}
            isTopConcept={confirmIsTopConcept}
            conceptList={confirmConceptList}
            defaultLang={defaultLang}
            onSure={this.onPropertySure}
            onNotSure={this.onNotSure} />
        }
        {conceptChanged && <CancelChange onExitChange={this.onExitChange}
          message={[
            "Somebody else has just made a change that may have affected your intended action.",
            "Therefore we have not performed the last action.",
            "Please check if still applicable and redo if so."
          ]} />
        }
      </React.Fragment>
    );
  }

}

TaxonomyEditorContainer.propTypes = {
  id: PropTypes.string.isRequired,
  history: PropTypes.object,
  location: PropTypes.object,
  entitlements: PropTypes.array.isRequired,
  skosXL: PropTypes.bool.isRequired,
  collections: PropTypes.array,
  taxonomies: PropTypes.array,
  projectUsers: PropTypes.array,
  ontologyProjects: PropTypes.array,
  toggleTaxonomyDetail: PropTypes.func,
  graph: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  project: PropTypes.string.isRequired,
  defaultLang: PropTypes.string.isRequired
};

export default withRouter(withGS(TaxonomyEditorContainer, ["ALL"], null, {}));